// ⬡B:aba_shared.ccwa_core.components.coder_check_in_badge:COMPONENT:banana_pepper_active_identity:20260718⬡
// ⬡B:aba_shared.ccwa_core.components.coder_check_in_badge:FIX:render_the_real_coder_plus_lineage_identity:20260729⬡
// CoderCheckInBadge — renders the current temporary-coder check-in.
// Pure render (90/10): takes an already-normalized check-in object (from
// normalizeCheckIn / useCoderCheckIn) and displays the doctrine fields —
// active identity (coder name + lineage), lineage chain, status, current work,
// cycle stamp, and filtered reporting counts. No logic, no fetch, no founder
// data: everything shown is derived from the normalized payload. All styles
// inline, no external stylesheet dependency, matching the ccwa-core convention.

import { CODER_STATUS, REPORT_BAND } from '../lib/coderCheckIn.js';

const STATUS_ACCENT = {
  [CODER_STATUS.IDLE]: 'rgba(255,255,255,0.45)',
  [CODER_STATUS.CHECKED_IN]: '#70a8ff',
  [CODER_STATUS.WORKING]: '#4ade80',
  [CODER_STATUS.BLOCKED]: '#ef6b6b',
  [CODER_STATUS.HANDED_OFF]: '#c9a8e0',
  [CODER_STATUS.UNKNOWN]: 'rgba(255,255,255,0.35)',
};

export function CoderCheckInBadge({ checkIn }) {
  if (!checkIn || !checkIn.present) {
    return (
      <div style={emptyStyle}>
        No temporary coder checked in. The pen is idle.
      </div>
    );
  }

  const { identity, lineage, status, currentWork, report, cycle } = checkIn;
  const accent = STATUS_ACCENT[status] || STATUS_ACCENT[CODER_STATUS.UNKNOWN];

  return (
    <div style={wrapStyle}>
      <div style={rowStyle}>
        <span style={{ ...dotStyle, background: accent }} />
        <span style={identityStyle} title={identity.complete ? undefined : 'coder name + lineage incomplete'}>
          {identity.label}
        </span>
        <span style={{ ...statusStyle, color: accent, borderColor: `${accent}55` }}>
          {status}
        </span>
      </div>

      {currentWork ? (
        <div style={workStyle}>{currentWork}</div>
      ) : (
        <div style={{ ...workStyle, opacity: 0.4 }}>no current work reported</div>
      )}

      {lineage.length > 0 && (
        <div style={lineageStyle}>
          <span style={labelStyle}>lineage</span>
          <span style={lineageChainStyle}>
            {lineage.map((c) => c.label).join(' → ')}
          </span>
        </div>
      )}

      {(report.items.length > 0 || report.escalations > 0 || report.suppressed > 0) && (
        <div style={reportRowStyle}>
          {report.escalations > 0 && (
            <span style={{ ...tagStyle, color: '#ef6b6b', borderColor: '#ef6b6b55' }}>
              {report.escalations} escalated
            </span>
          )}
          <span style={tagStyle}>
            {report.items.filter((i) => i.band === REPORT_BAND.ROUTINE).length} routine
          </span>
          {report.suppressed > 0 && (
            <span style={{ ...tagStyle, opacity: 0.5 }}>
              {report.suppressed} suppressed
            </span>
          )}
        </div>
      )}

      {cycle.stamp && (
        <div style={cycleStyle} title={cycle.stamp}>
          cycle {cycle.date || cycle.stamp}
        </div>
      )}
    </div>
  );
}

const font = 'system-ui, -apple-system, sans-serif';

const wrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '10px 12px',
  borderRadius: '10px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.09)',
  fontFamily: font,
  color: '#e2e8f0',
};

const emptyStyle = {
  padding: '10px 12px',
  fontSize: '11px',
  color: 'rgba(255,255,255,0.35)',
  fontFamily: font,
};

const rowStyle = { display: 'flex', alignItems: 'center', gap: '8px' };

const dotStyle = { width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0 };

const identityStyle = { fontSize: '13px', fontWeight: 700, flex: 1, minWidth: 0 };

const statusStyle = {
  fontSize: '10px',
  fontWeight: 600,
  padding: '1px 7px',
  borderRadius: '7px',
  border: '1px solid',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

const workStyle = { fontSize: '12px', lineHeight: 1.4, color: 'rgba(255,255,255,0.8)' };

const lineageStyle = { display: 'flex', alignItems: 'baseline', gap: '6px', fontSize: '11px' };

const labelStyle = {
  fontSize: '9px',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'rgba(255,255,255,0.4)',
  flexShrink: 0,
};

const lineageChainStyle = { color: 'rgba(255,255,255,0.65)', minWidth: 0 };

const reportRowStyle = { display: 'flex', gap: '6px', flexWrap: 'wrap' };

const tagStyle = {
  fontSize: '10px',
  fontWeight: 600,
  padding: '1px 7px',
  borderRadius: '7px',
  color: 'rgba(255,255,255,0.6)',
  border: '1px solid rgba(255,255,255,0.12)',
};

const cycleStyle = {
  fontSize: '10px',
  color: 'rgba(255,255,255,0.4)',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

export default CoderCheckInBadge;
